import { computed, inject, Injectable, signal } from '@angular/core';
import type { CardList } from '@framework/analytics/models/layout.model';
import { CardService } from '@framework/analytics/services/card.service';
import { DataService } from '@framework/analytics/services/data.service';
import { log } from '@jepto/shared/utils/logger';
import {
	cardHasCaptureData,
	cardNeedsAsyncCaptureData,
} from '../utils/card-capture-readiness.util';
import {
	CAPTURE_READY_TIMEOUT_MS,
	isCaptureMode,
} from '../utils/report-capture.util';

/**
 * Tracks when the analytics viewer is safe to screenshot or export to PDF.
 * Active only when the URL includes `?capture=1` (set by backend Playwright jobs).
 */
@Injectable({ providedIn: 'root' })
export class ReportCaptureReadinessService {
	private readonly cardService = inject(CardService);
	private readonly dataService = inject(DataService);

	readonly isCaptureMode: boolean;

	private readonly viewReady = signal(false);
	private readonly pageStarted = signal(false);
	private readonly paintReady = signal(false);
	private readonly timedOut = signal(false);
	private readonly pendingCardIds = signal<Set<string>>(new Set());

	readonly captureReady = computed(() => {
		if (!this.isCaptureMode) {
			return false;
		}
		if (this.timedOut()) {
			return true;
		}
		if (!this.viewReady() || !this.pageStarted()) {
			return false;
		}
		if (this.pendingCardIds().size > 0) {
			return false;
		}
		return this.paintReady();
	});

	private timeoutHandle: ReturnType<typeof setTimeout> | undefined;

	constructor() {
		this.isCaptureMode = isCaptureMode();
		if (this.isCaptureMode) {
			this.timeoutHandle = setTimeout(() => {
				this.timedOut.set(true);
				log(
					'[Analytics][Capture] readiness timeout — signaling ready for capture'
				);
			}, CAPTURE_READY_TIMEOUT_MS);
		}
	}

	resetForView(): void {
		if (!this.isCaptureMode) {
			return;
		}
		this.viewReady.set(false);
		this.pageStarted.set(false);
		this.paintReady.set(false);
		this.pendingCardIds.set(new Set());
	}

	markViewReady(): void {
		if (!this.isCaptureMode) {
			return;
		}
		this.viewReady.set(true);
	}

	beginPage(cards: CardList[]): void {
		if (!this.isCaptureMode) {
			return;
		}

		this.pageStarted.set(false);
		this.paintReady.set(false);

		const pending = new Set<string>();
		for (const card of cards) {
			const config = this.cardService.cardConfig(card.cardId)();
			if (!cardNeedsAsyncCaptureData(card, config)) {
				continue;
			}
			if (cardHasCaptureData(card, config, this.dataService)) {
				continue;
			}
			pending.add(card.cardId);
		}

		this.pendingCardIds.set(pending);
		this.pageStarted.set(true);

		if (pending.size === 0) {
			this.schedulePaintReady();
		}
	}

	markCardSettled(cardId: string): void {
		if (!this.isCaptureMode || !cardId?.trim()) {
			return;
		}

		const pending = new Set(this.pendingCardIds());
		if (!pending.delete(cardId)) {
			return;
		}

		this.pendingCardIds.set(pending);
		if (pending.size === 0) {
			this.schedulePaintReady();
		}
	}

	private schedulePaintReady(): void {
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				this.paintReady.set(true);
			});
		});
	}
}
